import { Button, Col, Field, Input, Select, Text } from "@rally/ui-kit";
import { useState } from "react";
import { say } from "../../localization/say.ts";
import { act } from "../../net/rally.ts";
import type { EditableApp } from "./app-controls.tsx";

type Rule = EditableApp["shipping"][number];
type Draft = Pick<Rule, "project" | "repo" | "branch" | "target"> & { gate: string; existing: boolean };

export function AppShippingControls({
  app,
  reload,
}: {
  app: Pick<EditableApp, "id" | "placements" | "repositories" | "shipping">;
  reload: () => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(false);
  const projects = app.placements.filter((one) => one.context === "project" && one.contextId);
  const change = (next: Partial<Draft>) => setDraft((held) => (held ? { ...held, ...next } : held));
  const duplicate =
    draft &&
    !draft.existing &&
    app.shipping.some(
      (one) => one.project === draft.project && one.repo === draft.repo && one.branch === draft.branch.trim(),
    );
  const save = async () => {
    if (!draft || duplicate) return;
    setSaving(true);
    setFailure(false);
    try {
      await act("app.setShipping", {
        app: app.id,
        project: draft.project,
        repo: draft.repo,
        branch: draft.branch.trim(),
        target: draft.target.trim(),
        gate: draft.gate,
      });
      setDraft(null);
      reload();
    } catch {
      setFailure(true);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Col gap={2} testID="app-shipping" style={{ minWidth: 0 }}>
      <Text typeName="bodySemibold">{say("apps.automaticShipping")}</Text>
      {app.shipping.map((rule, index) => (
        <Col
          gap={1}
          key={`${rule.project}:${rule.repo}:${rule.branch}`}
          testID={`app-shipping-rule-${index}`}
        >
          <Text typeName="caption" numberOfLines={3}>
            {rule.projectName} / {rule.repo}
          </Text>
          <Text typeName="body" numberOfLines={3}>
            {say("apps.shipBranch")}: {rule.branch}
          </Text>
          <Text typeName="body" numberOfLines={3}>
            {say("apps.shipTarget")}: {rule.target}
          </Text>
          <Text typeName="caption" numberOfLines={3}>
            {say("apps.shipGate")}: {rule.gate ?? say("apps.shippingNoGate")}
          </Text>
          <Button
            variant="ghost"
            style={{ minHeight: 44 }}
            disabled={saving || !!draft}
            testID={`app-shipping-edit-${index}`}
            onPress={() => {
              setFailure(false);
              setDraft({ ...rule, gate: rule.gate ?? "", existing: true });
            }}
          >
            {say("apps.editShipping")}
          </Button>
        </Col>
      ))}
      {!draft ? (
        <Button
          variant="outline"
          style={{ minHeight: 44 }}
          testID="app-shipping-add"
          disabled={projects.length === 0 || app.repositories.length === 0}
          onPress={() => {
            setFailure(false);
            setDraft({ project: "", repo: "", branch: "", target: "", gate: "", existing: false });
          }}
        >
          {say("apps.addShipping")}
        </Button>
      ) : (
        <Col gap={3} testID="app-shipping-editor">
          <Field label={say("apps.project")}>
            <Select
              testID="app-shipping-project"
              accessibilityLabel={say("apps.project")}
              placeholder={say("apps.chooseShippingProject")}
              value={draft.project}
              disabled={saving || draft.existing}
              onChange={(project) => change({ project })}
              options={projects.map((one) => ({
                value: one.contextId ?? "",
                label: one.contextName ?? one.contextId ?? "",
              }))}
            />
          </Field>
          <Field label={say("apps.repository")}>
            <Select
              testID="app-shipping-repo"
              accessibilityLabel={say("apps.repository")}
              placeholder={say("apps.chooseShippingRepo")}
              value={draft.repo}
              disabled={saving || draft.existing}
              onChange={(repo) => change({ repo })}
              options={app.repositories.map((one) => ({ value: one.name, label: one.name }))}
            />
          </Field>
          <Field label={say("apps.shipBranch")} hint={say("apps.shippingIdentityHint")}>
            <Input
              testID="app-shipping-branch"
              accessibilityLabel={say("apps.shipBranch")}
              value={draft.branch}
              onChangeText={(branch) => change({ branch })}
              editable={!saving && !draft.existing}
              multiline={false}
              autoCapitalize="none"
              style={{ minHeight: 46, alignItems: "stretch" }}
            />
          </Field>
          <Field label={say("apps.shipTarget")}>
            <Input
              testID="app-shipping-target"
              accessibilityLabel={say("apps.shipTarget")}
              value={draft.target}
              onChangeText={(target) => change({ target })}
              editable={!saving}
              multiline={false}
              autoCapitalize="none"
              style={{ minHeight: 46, alignItems: "stretch" }}
            />
          </Field>
          <Field label={say("apps.shipGate")} hint={say("apps.shippingGateHint")}>
            <Input
              testID="app-shipping-gate"
              accessibilityLabel={say("apps.shipGate")}
              value={draft.gate}
              onChangeText={(gate) => change({ gate })}
              editable={!saving}
              multiline={false}
              autoCapitalize="none"
              style={{ minHeight: 46, alignItems: "stretch" }}
            />
          </Field>
          {duplicate ? <Text color="danger">{say("apps.shippingDuplicate")}</Text> : null}
          {failure ? (
            <Text color="danger" accessibilityRole="alert" testID="app-shipping-error">
              {say("apps.saveFailed")}
            </Text>
          ) : null}
          <Col gap={2}>
            <Button
              style={{ minHeight: 44 }}
              testID="app-shipping-save"
              disabled={
                saving ||
                !!duplicate ||
                !draft.project ||
                !draft.repo ||
                !draft.branch.trim() ||
                !draft.target.trim()
              }
              onPress={() => void save()}
            >
              {say("apps.saveShipping")}
            </Button>
            <Button
              variant="ghost"
              style={{ minHeight: 44 }}
              testID="app-shipping-cancel"
              disabled={saving}
              onPress={() => setDraft(null)}
            >
              {say("apps.cancel")}
            </Button>
          </Col>
        </Col>
      )}
      {projects.length === 0 || app.repositories.length === 0 ? (
        <Text typeName="caption">{say("apps.shippingScopeNeeded")}</Text>
      ) : null}
    </Col>
  );
}
